import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { EmptyState, PageHeader } from "@oms/ui";
import { PageShell } from "@/components/pages/page-shell";
import {
  SETTINGS_NAV,
  isSettingsSection,
  type SettingsSection,
} from "@/components/settings/settings-nav";
import { SettingsSidebar } from "@/components/settings/settings-sidebar";
import { RolesSection } from "@/features/settings/roles/roles-section";
import { IntegrationsSection } from "@/features/settings/sections/integrations-section";
import { PlaceholderSection } from "@/features/settings/sections/placeholder-section";
import { UsersSection } from "@/features/settings/sections/users-section";
import { WorkspaceSection } from "@/features/settings/sections/workspace-section";
import { useCan } from "@/lib/permissions";
import { useAuthStore } from "@/stores/auth-store";

interface SettingsSearch {
  /** Optional so links to /app/settings need no search params. */
  section?: SettingsSection;
}

export const Route = createFileRoute("/app/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    section: isSettingsSection(search.section) ? search.section : "workspace",
  }),
  component: SettingsPage,
});

/**
 * Settings is a single route whose sections are rendered from the member's
 * permissions rather than from separate guarded routes — the sub-nav only
 * lists what the role can reach, and the section itself re-checks.
 */
function SettingsPage() {
  const { section = "workspace" } = Route.useSearch();
  const navigate = useNavigate({ from: "/app/settings" });
  const can = useCan();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  const navItem = SETTINGS_NAV.flatMap((group) => group.items).find(
    (item) => item.section === section,
  );
  const allowed =
    navItem !== undefined &&
    (!navItem.superAdminOnly || isSuperAdmin) &&
    can(navItem.permission);

  const select = (next: SettingsSection) =>
    void navigate({ search: { section: next } });

  return (
    <PageShell>
      <PageHeader
        title="Settings"
        description="Manage your workspace, team access and connections."
      />

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <SettingsSidebar active={section} onSelect={select} />

        <div className="min-w-0 rounded-xl border border-border bg-surface p-5 sm:p-6">
          {allowed ? (
            <SettingsSectionBody section={section} />
          ) : (
            <EmptyState
              title="You do not have access to this section"
              description="Your role does not include permission to view this part of settings. Ask an admin if you think this is a mistake."
            />
          )}
        </div>
      </div>
    </PageShell>
  );
}

function SettingsSectionBody({ section }: { section: SettingsSection }) {
  switch (section) {
    case "workspace":
      return <WorkspaceSection />;
    case "users":
      return <UsersSection />;
    case "roles":
      return <RolesSection />;
    case "integrations":
      return <IntegrationsSection />;
    case "activity":
      return (
        <PlaceholderSection
          title="Activity"
          description="An audit trail of who changed what in this workspace."
          body="Audit events are already being recorded server-side. This view will surface them next."
        />
      );
    case "billing":
      return (
        <PlaceholderSection
          title="Billing"
          description="Plan, invoices and payment method."
          body="Billing is not enabled for this workspace yet."
        />
      );
    case "preferences":
      return (
        <PlaceholderSection
          title="Preferences"
          description="Defaults applied across this workspace."
          body="Workspace preferences will land alongside the notifications module."
        />
      );
  }
}
